import { formatDateUz } from '@/lib/relative-date'
import { formatSom, PayoutStatus } from '@ayub/shared'
import { cn } from '@/lib/utils'
import { PAYOUTS_PAGE_SIZE, fetchPayoutCard } from './api'
import { PayoutsNeutralEmptyState } from './EmptyStates'
import { PayoutsErrorBanner, PayoutsRetryState } from './ErrorStates'
import type { HistoryStatusFilter } from './hooks'
import { usePayoutHistory } from './hooks'
import { Pagination } from './Pagination'
import { PayoutCardCell } from './PayoutCardCell'
import { PAYOUT_STATUS_LABELS, PAYOUT_STATUS_META } from './statusMeta'
import { Skeleton } from '@/components/ui/skeleton'

export interface PayoutHistoryViewProps {
  status: HistoryStatusFilter
  onStatusChange: (status: HistoryStatusFilter) => void
  page: number
  onPageChange: (page: number) => void
}

/**
 * "📜 Tarix" tabi — `docs/03-app-flow.md`: o'tgan to'lovlar, eng yangi birinchi. `REQUESTED`
 * (hali kutayotgan) hech qachon bu yerda ko'rinmaydi — u "So'rovlar" tabida.
 *
 * ⚠️ DIZAYN QARORI (hujjatda aniqlanmagan): kontrakt bitta `status` filtrini qabul qiladi
 * (bir nechtasini birga emas), shuning uchun "hammasi" o'rniga ikkita aniq chip berilgan —
 * `To'landi` (standart) va `Bekor qilingan`. Bu ikkalasini alohida ko'rish adminga foydali:
 * "nega bu bloger to'lanmadi?" savoliga sabab bilan javob beradi.
 */
export function PayoutHistoryView({
  status,
  onStatusChange,
  page,
  onPageChange,
}: PayoutHistoryViewProps) {
  const list = usePayoutHistory(status, page)

  if (list.isLoading) {
    return (
      <div className="flex flex-col gap-3">
        <HistoryStatusChips status={status} onChange={onStatusChange} />
        <PayoutHistorySkeleton />
      </div>
    )
  }

  if (list.error && !list.data) {
    return (
      <div className="flex flex-col gap-3">
        <HistoryStatusChips status={status} onChange={onStatusChange} />
        <PayoutsRetryState error={list.error} onRetry={() => void list.refetch()} />
      </div>
    )
  }

  const items = list.data?.items ?? []
  const total = list.data?.total ?? 0

  return (
    // `aria-busy` — fon rejimida qayta so'ralayotganda skrinriderga signal beradi.
    <div className="flex flex-col gap-3" aria-busy={list.isFetching}>
      <HistoryStatusChips status={status} onChange={onStatusChange} />

      {list.error && (
        <PayoutsErrorBanner
          error={list.error}
          isFetching={list.isFetching}
          onRetry={() => void list.refetch()}
        />
      )}

      {total === 0 ? (
        <PayoutsNeutralEmptyState
          message={
            status === PayoutStatus.PAID
              ? "Hali birorta to'lov amalga oshirilmagan."
              : "Bekor qilingan so'rov yo'q."
          }
        />
      ) : (
        <>
          {/* Desktop / tablet: jadval */}
          <div className="hidden overflow-x-auto rounded-card border border-app-border sm:block">
            <table className="w-full border-collapse text-sm">
              <thead className="sticky top-0 bg-app-card">
                <tr>
                  <th className="border-b border-app-border px-3 py-2 text-left font-semibold text-app-hint">
                    Bloger
                  </th>
                  <th className="border-b border-app-border px-3 py-2 text-left font-semibold text-app-hint">
                    Summa
                  </th>
                  <th className="border-b border-app-border px-3 py-2 text-left font-semibold text-app-hint">
                    Karta
                  </th>
                  <th className="border-b border-app-border px-3 py-2 text-left font-semibold text-app-hint">
                    Sana
                  </th>
                  <th className="border-b border-app-border px-3 py-2 text-left font-semibold text-app-hint">
                    Holat
                  </th>
                </tr>
              </thead>
              <tbody>
                {items.map((payout) => {
                  const meta = PAYOUT_STATUS_META[payout.status]
                  const Icon = meta.icon
                  return (
                    <tr key={payout.id} className="border-b border-app-border last:border-b-0">
                      <td className="px-3 py-2">
                        <div className="flex flex-col">
                          <span className="font-medium text-app-text">{payout.bloggerName}</span>
                          <span className="text-xs text-app-hint">@{payout.instagramUsername}</span>
                        </div>
                      </td>
                      <td className="px-3 py-2">
                        <span className="tabular font-medium text-app-text">
                          {formatSom(payout.amount)}
                        </span>
                      </td>
                      <td className="px-3 py-2">
                        <PayoutCardCell
                          maskedCard={payout.cardNumberMasked}
                          onReveal={() => fetchPayoutCard(payout.id)}
                        />
                      </td>
                      <td className="tabular px-3 py-2 text-app-hint">
                        {formatDateUz(payout.paidAt ?? payout.requestedAt)}
                      </td>
                      <td className="px-3 py-2">
                        <span
                          className={cn(
                            'inline-flex items-center gap-1 rounded-badge px-2 py-0.5 text-xs font-medium',
                            meta.textClass,
                            meta.subtleClass,
                          )}
                        >
                          <Icon size={12} aria-hidden="true" />
                          {PAYOUT_STATUS_LABELS[payout.status]}
                        </span>
                        {payout.note && (
                          <p
                            className="mt-0.5 max-w-[220px] truncate text-xs text-app-hint"
                            title={payout.note}
                          >
                            {payout.note}
                          </p>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>

          {/* Mobil: kartochkalar ro'yxati */}
          <div className="flex flex-col gap-2 sm:hidden">
            {items.map((payout) => {
              const meta = PAYOUT_STATUS_META[payout.status]
              const Icon = meta.icon
              return (
                <div
                  key={payout.id}
                  className="flex flex-col gap-2 rounded-card border border-app-border bg-app-card p-3"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex flex-col">
                      <span className="font-medium text-app-text">{payout.bloggerName}</span>
                      <span className="text-xs text-app-hint">@{payout.instagramUsername}</span>
                    </div>
                    <span
                      className={cn(
                        'inline-flex shrink-0 items-center gap-1 rounded-badge px-2 py-0.5 text-xs font-medium',
                        meta.textClass,
                        meta.subtleClass,
                      )}
                    >
                      <Icon size={12} aria-hidden="true" />
                      {PAYOUT_STATUS_LABELS[payout.status]}
                    </span>
                  </div>
                  <div className="flex items-center justify-between gap-2">
                    <span className="tabular font-semibold text-app-text">
                      {formatSom(payout.amount)}
                    </span>
                    <span className="tabular text-xs text-app-hint">
                      {formatDateUz(payout.paidAt ?? payout.requestedAt)}
                    </span>
                  </div>
                  <PayoutCardCell
                    maskedCard={payout.cardNumberMasked}
                    onReveal={() => fetchPayoutCard(payout.id)}
                  />
                  {payout.note && <p className="text-xs text-app-hint">{payout.note}</p>}
                </div>
              )
            })}
          </div>

          <Pagination
            page={page}
            pageSize={PAYOUTS_PAGE_SIZE}
            total={total}
            onPageChange={onPageChange}
          />
        </>
      )}
    </div>
  )
}

function HistoryStatusChips({
  status,
  onChange,
}: {
  status: HistoryStatusFilter
  onChange: (status: HistoryStatusFilter) => void
}) {
  return (
    <div role="group" aria-label="Tarix filtri" className="flex gap-2">
      <button
        type="button"
        onClick={() => onChange(PayoutStatus.PAID)}
        aria-pressed={status === PayoutStatus.PAID}
        className={cn(
          'inline-flex h-9 items-center rounded-badge border px-3 text-sm font-medium transition-colors duration-150 ease-out',
          status === PayoutStatus.PAID
            ? 'border-brand bg-brand-subtle text-brand'
            : 'border-app-border text-app-hint hover:text-app-text',
        )}
      >
        To&apos;langan
      </button>
      <button
        type="button"
        onClick={() => onChange(PayoutStatus.CANCELLED)}
        aria-pressed={status === PayoutStatus.CANCELLED}
        className={cn(
          'inline-flex h-9 items-center rounded-badge border px-3 text-sm font-medium transition-colors duration-150 ease-out',
          status === PayoutStatus.CANCELLED
            ? 'border-brand bg-brand-subtle text-brand'
            : 'border-app-border text-app-hint hover:text-app-text',
        )}
      >
        Bekor qilingan
      </button>
    </div>
  )
}

function PayoutHistorySkeleton() {
  return (
    <div className="flex flex-col gap-3">
      <div className="hidden overflow-hidden rounded-card border border-app-border sm:block">
        {Array.from({ length: 6 }, (_, rowIndex) => (
          <div
            key={rowIndex}
            className="flex h-14 items-center gap-4 border-b border-app-border px-3 last:border-b-0"
          >
            <Skeleton className="h-3 w-32" />
            <Skeleton className="h-3 w-20" />
            <Skeleton className="h-3 w-24" />
            <Skeleton className="h-3 w-16" />
            <Skeleton className="h-3 w-16" />
          </div>
        ))}
      </div>
      <div className="flex flex-col gap-2 sm:hidden">
        {Array.from({ length: 4 }, (_, index) => (
          <Skeleton key={index} className="h-[110px]" />
        ))}
      </div>
    </div>
  )
}
